import { useState, useEffect } from "react";
import { History, Search, Filter, CheckCircle, XCircle, Clock, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icons
const FacebookIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

export default function HistoryPage() {
  const [posts, setPosts] = useState([]);
  const [publicationLogs, setPublicationLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsRes, logsRes] = await Promise.all([
        axios.get(`${API}/posts`),
        axios.get(`${API}/history`)
      ]);
      setPosts(postsRes.data);
      setPublicationLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      published: { className: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100", label: "Publié", icon: CheckCircle },
      scheduled: { className: "bg-amber-100 text-amber-700 hover:bg-amber-100", label: "Planifié", icon: Clock },
      draft: { className: "bg-slate-100 text-slate-700 hover:bg-slate-100", label: "Brouillon", icon: Clock },
      failed: { className: "bg-red-100 text-red-700 hover:bg-red-100", label: "Échoué", icon: XCircle },
      partial: { className: "bg-orange-100 text-orange-700 hover:bg-orange-100", label: "Partiel", icon: RefreshCw },
      success: { className: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100", label: "Succès", icon: CheckCircle },
      pending: { className: "bg-blue-100 text-blue-700 hover:bg-blue-100", label: "En attente", icon: Clock }
    };
    const variant = variants[status] || variants.draft;
    const IconComponent = variant.icon;
    return (
      <Badge className={`${variant.className} gap-1`}>
        <IconComponent className="w-3 h-3" />
        {variant.label}
      </Badge>
    );
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case "facebook":
        return <div className="w-6 h-6 rounded bg-[#1877F2] flex items-center justify-center text-white"><FacebookIcon /></div>;
      case "instagram":
        return <div className="w-6 h-6 rounded bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888] flex items-center justify-center text-white"><InstagramIcon /></div>;
      case "tiktok":
        return <div className="w-6 h-6 rounded bg-black flex items-center justify-center text-white"><TikTokIcon /></div>;
      default:
        return null;
    }
  };

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || post.status === statusFilter;
    const matchesPlatform = platformFilter === "all" || post.platforms.includes(platformFilter);
    return matchesSearch && matchesStatus && matchesPlatform;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="history-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Historique</h1>
        <p className="text-muted-foreground mt-1">Consultez l'historique de vos publications</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher dans vos posts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="published">Publié</SelectItem>
                <SelectItem value="scheduled">Planifié</SelectItem>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="failed">Échoué</SelectItem>
              </SelectContent>
            </Select>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="platform-filter">
                <SelectValue placeholder="Plateforme" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Posts List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5" />
            Vos publications ({filteredPosts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse flex items-center gap-4 p-4 rounded-lg bg-muted/50">
                  <div className="w-12 h-12 rounded-lg bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                  <div className="h-6 w-20 bg-muted rounded" />
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">
                {searchQuery || statusFilter !== "all" || platformFilter !== "all"
                  ? "Aucun résultat pour ces filtres"
                  : "Aucune publication pour le moment"
                }
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPosts.map((post, index) => (
                <div 
                  key={post.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors animate-fade-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                  data-testid={`history-post-${post.id}`}
                >
                  {/* Platforms */}
                  <div className="flex-shrink-0 flex gap-1">
                    {post.platforms.map((platform) => (
                      <div key={platform}>
                        {getPlatformIcon(platform)}
                      </div>
                    ))}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium line-clamp-2">
                      {post.content.slice(0, 100)}{post.content.length > 100 ? '...' : ''}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span>
                        {format(new Date(post.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                      </span>
                      {post.media && post.media.length > 0 && (
                        <span className="flex items-center gap-1">
                          {post.media.length} média{post.media.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex-shrink-0">
                    {getStatusBadge(post.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Publication Logs */}
      {publicationLogs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Journal des publications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {publicationLogs.slice(0, 10).map((log, index) => (
                <div 
                  key={log.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 animate-fade-in"
                  style={{ animationDelay: `${index * 0.03}s` }}
                >
                  {getPlatformIcon(log.platform)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium capitalize">{log.platform}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(log.published_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                    </p>
                  </div>
                  {getStatusBadge(log.status)}
                  {log.error_message && (
                    <p className="text-xs text-red-600 max-w-[200px] truncate" title={log.error_message}>
                      {log.error_message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
